<template>
    <div>
        <v-card
            style="outline: grey 1px solid"
            class="mb-4 mx-1"
        >
            <div>
                <v-card-title color="grey lighten">
                    <v-layout
                        text-h6
                        align-items-center
                        justify-space-between
                    >
                        <div
                            class="mt-1 text-h6"
                            data-testid="unit-name"
                            style="width: 52vw"
                        >
                          <span>Unit {{ getUnitName(unit) }}</span>
                        </div>

                        <!-- For Promise To Pay -->
                        <v-switch
                            v-if="promiseToPay && unitIsEligible"
                            data-testid="promise-add-to-cart-switch"
                            class="promise-add-to-cart-switch pt-0 mt-0"
                            @change="togglePromiseToPay(unit)"
                            :label="selectedPromiseId === unit.next_month[0].UnitID ? 'Selected' : 'Select unit'"
                            hide-details
                            v-model="unitInCart"
                        />

                        <!-- For Payment Plan-->
                        <v-switch
                            v-else-if="unitIsEligible"
                            data-testid="add-to-cart-switch"
                            class="add-to-cart-switch pt-0 mt-0"
                            @change="handlePaymentPlan(unit)"
                            :label="unitInCart ? 'Selected' : 'Select unit'"
                            hide-details
                            v-model="unitInCart"
                        />
                    </v-layout>
                </v-card-title>
                <v-divider />
                <transition name="fade-transition" mode="out-in">
                    <v-layout
                        v-if="loading"
                        justify-center
                        key="load-state"
                    >
                        <v-progress-circular
                            :size="50"
                            :width="5"
                            indeterminate
                            class="my-4"
                        />
                    </v-layout>

                    <!-- display PP button or unavailable message -->
                    <v-layout
                        column
                        class="mt-4 mb-2 mx-3"
                        v-else
                        key="plan-details"
                    >
                        <div
                            v-if="!unitIsEligible"
                            class="pb-4 subheading font-weight-regular"
                            data-testid="not-eligible"
                        >
                            <span v-if="promiseToPay">
                                This unit is not eligible for promise to pay.
                            </span>
                            <span v-else>
                                This unit is not eligible for a payment plan.
                            </span>
                        </div>
                        <v-layout
                            v-else
                            align-start
                            data-testid="breakdown-container"
                        >
                            <v-flex>
                                <promise-breakdown
                                    v-if="promiseToPay"
                                    :unit="thisMonth"
                                    :months="1"
                                    :chargePeriod="'Current Month'"
                                />
                                <payment-breakdown
                                    v-else
                                    :unit="unit"
                                    :months="2"
                                    :monthlyCost="planMonthsTwo"
                                />
                            </v-flex>
                            <v-flex>
                                <promise-breakdown
                                    v-if="promiseToPay"
                                    :unit="nextMonth"
                                    :months="2"
                                    :chargePeriod="'Current + Next Month'"
                                />
                                <payment-breakdown
                                    v-else
                                    :unit="unit"
                                    :months="3"
                                    :monthlyCost="planMonthsThree"
                                />
                            </v-flex>
                        </v-layout>
                    </v-layout>
                </transition>
            </div>
        </v-card>
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import PaymentBreakdown from "@/modules/accounts-receivable/views/payment-plan/partials/PaymentBreakdown";
import PromiseBreakdown from "@/modules/accounts-receivable/views/promise-to-pay/partials/PromiseBreakdown";

export default {
  name: "SelectUnitCard",
  components: { PromiseBreakdown, PaymentBreakdown },
  props: {
    unit: {
      type: [Object, Array]
    }
  },
  data() {
    return {
      loading: false,
      monthlyCost: ""
    };
  },
  methods: {
    getUnitName(unit) {
      if (this.promiseToPay) {
        return this.getUnitNamePromiseToPay(unit);
      } else {
        return this.getUnitNamePaymentPlan(unit);
      }
    },

    getUnitNamePromiseToPay(unit) {
      if (!unit) {
        return "";
      }
      return this.getUnitDetails(unit).unit_name;
    },

    getUnitNamePaymentPlan(unit) {
      if (!unit || !unit.facility_unit) {
        return "";
      }
      return unit.facility_unit.name;
    },

    handlePaymentPlan(plan) {
      if (!plan.in_cart) {
        this.removePlanFromCart();
      } else {
        this.$store.commit("updateMonthlyCost", this.monthlyCost);
        this.$store.commit("updatePlanCart", plan);
      }
    },

    togglePromiseToPay(promise) {
      if (!promise.in_cart) {
        this.$store.commit("updatePromiseCart", "");
      } else {
        this.$store.commit("updatePromiseCart", promise);
      }
    },

    removePlanFromCart() {
      if (this.promiseToPay) {
        this.$store.commit("resetPromiseCart");
      } else {
        this.$store.commit("resetPlanCart");
      }
    },

    getUnitDetails(unit) {
      return {
        unit_name: unit.this_month[0].sUnitName,
        unit_id: unit.this_month[0].UnitID,
        ledger_id: unit.this_month[0].LedgerID
      };
    },

    handleMonthlyRate() {
      this.loading = true;
      let monthlyRatePayload = {
        facility_id: this.$route.params.facilityId,
        external_tenant_id: this.$route.params.tenantId,
        external_ledger_id: this.unit.pms_ledger_id
      };

      this.$store.dispatch("generateMonthlyRate", monthlyRatePayload)
        .then((resp) => {
          if (!resp.data || !resp.data.monthly_rate) {
            this.loading = false;
          }

          let planPayload = {
            total_balance: this.unit.total_amount_due.toFixed(2),
            monthly_rate: resp.data.monthly_rate.toString()
          };

          this.$store.dispatch("generatePlanCost", planPayload)
            .then((costResp) => {
              this.monthlyCost = costResp.data.plan_cost;
              this.loading = false;
            })
            .catch((costErr) => {
              this.$store.dispatch("handleError", costErr);
              this.loading = false;
            });
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
          this.loading = false;
        });
    }
  },
  computed: {
    ...mapState({
      planCart: state => state.accountsReceivable.planCart,
      planMonths: state => state.accountsReceivable.planMonths,
      planMonthlyCost: state => state.accountsReceivable.planMonthlyCost,
      promiseCart: state => state.accountsReceivable.promiseCart,
      promiseMonths: state => state.accountsReceivable.promiseMonths,
      switchStatus: state => state.accountsReceivable.switchStatus
    }),

    ...mapGetters({
      ledgersEligibleForPaymentPlan: "ledgersEligibleForPaymentPlan",
      ledgersEligibleForPromiseToPay: "ledgersEligibleForPromiseToPay"
    }),

    promiseToPay() {
      return this.$route.name === "promiseToPay";
    },

    selectedPromiseId() {
      return this.promiseCart.length ? this.promiseCart.next_month[0].UnitID : -1;
    },

    unitIsEligible() {
      if (this.promiseToPay) {
        const unitId = parseFloat(this.unit.this_month[0].LedgerID);
        return this.ledgersEligibleForPromiseToPay.includes(unitId);
      }
      if (!this.promiseToPay) {
        return this.ledgersEligibleForPaymentPlan.includes(this.unit.pms_ledger_id);
      }

      return null;
    },

    unitInCart: {
      get() {
        if (this.promiseToPay) {
          return this.promiseCart;
        }
        return this.switchStatus ? this.unit.in_cart : "";
      },
      set(newVal) {
        this.unit.in_cart = newVal;
        this.$store.commit("updateSwitchStatus", newVal);
      }
    },

    thisMonth() {
      return this.unit.this_month;
    },

    nextMonth() {
      return this.unit.next_month;
    },

    planMonthsTwo() {
      return this.planMonthlyCost === "" ? this.monthlyCost.two_months : this.planMonthlyCost.two_months;
    },

    planMonthsThree() {
      return this.planMonthlyCost === "" ? this.monthlyCost.three_months : this.planMonthlyCost.three_months;
    }

  },

  async created() {
    if (!this.promiseToPay) {
      this.handleMonthlyRate();
    }
  }
};
</script>
